<template>
  <aside id="drawer" class="c-drawer" data-test="drawer">
    <div id="drawerHeader" class="c-drawer__header">
      <div id="drawerToggle" class="c-drawer__toggle" @click="toggleDrawer()" data-test="drawer-toggle">
        <svg id="drawerToggleIcon" class="icon icon-chevron-right c-drawer__toggle-icon"><use xlink:href="#icon-chevron-right"></use></svg>
      </div>
      <div id="drawerActivity" class="c-drawer__activity" @click="toggleDrawer()" data-test="drawer-activity">
        <transition name="bounce-in-out">
          <div class="c-drawer__activity-count" v-show="queueLength > 0">{{ queueLength }}</div>
        </transition>
        <svg id="drawerActivityBolt" class="icon icon-activity c-drawer__activity-icon"><use xlink:href="#icon-activity"></use></svg>
        <div id="drawerActivityCircle" class="c-drawer__activity-circle"></div>
        <div class="c-drawer__activity-circle-pulse"></div>
      </div>
      <h5 id="drawerHeaderText" class="c-drawer__header-text" @click="animateActivity()">{{ $speak('heading') }}</h5>
    </div>


    <transition-group id="drawerList" class="c-drawer__list" name="list-fade" tag="div" v-if="$mq('md-min')" data-test="drawer-list">
      <drawer-list-item v-for="item in listItems" :data="item" :key="item.type + item.story.id"  :class="item.isNew ? 'list-slide-item c-drawer__new-item' : 'list-fade-item'"></drawer-list-item>
    </transition-group>


    <div id="drawerBody" class="c-drawer__body" data-test="drawer-body">
      <div class="c-drawer__aside"> 
        <div id="drawerFilters" class="c-drawer__filters">
          <filter-bar class="c-filterbar--drawer" name="feed" :gameTypes="feed.filters.gameTypes" @filtered="toggleGameTypeFilter($event)"></filter-bar>
        </div>
      </div>
      <div class="c-drawer__feed c-feed">
        <transition name="fade">
          <button :class="['btn', 'btn-sm', 'btn-primary', 'c-drawer__feed-update', { 'disabled' : queueLength <= 0 }]" @click="prependQueueToFeed()">{{ queueCount }}</button>
        </transition>

        <transition-group class="c-feed__body" name="list-fade" tag="div">
          <div v-for="item in allFeedData" :key="item.type + item.story.id" :class="item.isNew ? 'list-slide-item' : 'list-fade-item'">
            <feed-article :article="item" v-if="item.type === 'article'" :class="checkItemIsNew(item.isNew)"></feed-article>

            <feed-status :status="item" v-if="item.type === 'status'" :class="checkItemIsNew(item.isNew)"></feed-status>

            <feed-offer :offer="item" v-if="item.type === 'offer'" :class="checkItemIsNew(item.isNew)"></feed-offer>
          </div>
        </transition-group>

        <div class="c-feed__footer">
          <load-more :meta="allFeedMeta.cursor" :handler="getMoreFeed.bind(this, allFeedMeta.cursor.links.next)" v-if="allFeedMeta.cursor.links.next"></load-more>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
  // Vuex
  import { mapGetters, mapActions } from 'vuex';
  import { RECEIVED_FEED_GAMETYPES } from '@/store/mutation-types';
  
  // Lists
  import { getGameTypesAsList } from '@/lists';

  // Utilities
  import { debounce, randomInt, getScrollTop, setScrollTop } from '@/utilities';

  // local reference
  // Vue doesn't know this exists
  let local = {};

  export default {
    name: 'Drawer',
    
    speak: {
      prefix: 'app.components.Drawer'
    },

    components: {
      DrawerListItem: () => import(/* webpackChunkName: "DrawerListItem" */'./DrawerListItem.vue'),
      FilterBar: () => import(/* webpackChunkName: "FilterBar" */'./FilterBar.vue'),
      FeedArticle: () => import(/* webpackChunkName: "FeedArticle" */'./FeedArticle.vue'),
      FeedStatus: () => import(/* webpackChunkName: "FeedStatus" */'./FeedStatus.vue'),
      FeedOffer: () => import(/* webpackChunkName: "FeedOffer" */'./FeedOffer.vue'),
      LoadMore: () => import(/* webpackChunkName: "LoadMore" */'./LoadMore.vue')
    },

    data () {
      return {
        isAnimating: false,
        reset: false
      }
    },

    computed: {
      ...mapGetters([
        'drawer',
        'feed',
        'allFeedData',
        'allFeedMeta'
      ]),

      listItems () {
        let windowHeight = document.documentElement.clientHeight;
        let drawerHeaderHeight = 155;
        let listItemHeight = 100;

        let itemsToShow = Math.floor((windowHeight - drawerHeaderHeight) / listItemHeight);
        
        return this.allFeedData.filter(function (item, index) {
          return index <= (itemsToShow - 1); // - 1 because index starts at 0
        })
      },

      queueLength () {
        return this.feed.queue.length;
      },

      queueCount () {
        let count = this.queueLength;
        return count > 0 ? this.$speak('queue_updates', count) : this.$speak('queue_default');
      }
    },

    watch: {
      'drawer.isDrawerActive': function (value) {
        // Ensure window is > small media query
        if (!this.reset) {
          if (this.$mq('md-min')) {
            return value ? this.animateDrawerOpen() : this.animateDrawerClose();
          }
        }
      }
    },

    methods: {
      ...mapActions([
        'openDrawer',
        'closeDrawer',
        'getFeed',
        'getMoreFeed',
        'toggleGameTypeFilter',
        'prependQueueToFeed',
        'prependFeedItem'
      ]),

      checkItemIsNew (isNew) {
        return isNew ? 'c-pod--is-new' : '';
        // if (this.$mq('sm-max')) {
        //   return isNew ? 'c-pod--is-new' : '';
        // }
      },

      toggleDrawer () {
        if (!this.isAnimating) {
          if (this.drawer.isDrawerActive) {
            this.closeDrawer();
          } else {
            return this.openDrawer(0);
            // if (this.$mq('sm-max')) return this.openDrawer(0);
            // this.openDrawer();
          }
        }
      },

      initDrawerOpen () {
        local.collapsedWidth = local.el.drawer.offsetWidth;
        local.extendedWidth = local.collapsedWidth + 740;

        if (this.$mq('md-min AND md-max')) {
          local.activityIconMarginOpen = 70;
        }

        if (this.$mq('lg-min')) {
          local.activityIconMarginOpen = 76;
        }

        let timeline = new TimelineLite({ paused: true });

        timeline
          // Before drawer opens
          // Step 1
          .to(local.el.drawerHeader, local.speed.fast, { borderBottomWidth: 0 }, 'beforeOpen')
          .to(local.el.drawerHeaderText, local.speed.fast, { opacity: 0 }, 'beforeOpen')
          .to(local.el.drawerList, local.speed.fast, { display: 'none', opacity: 0 }, 'beforeOpen')
          // While drawer opens
          // Step 2
          .to(local.el.drawer, local.speed.fast, { width: local.extendedWidth }, 'whileOpening')
          .to(local.el.drawerActivity, local.speed.fast, { marginLeft: local.activityIconMarginOpen }, 'whileOpening')
          // After drawer opens
          // Step 3
          .to(local.el.drawerFilters, local.speed.fast, { display: 'block', opacity: 1 }, 'afterOpen')
          .to(local.el.drawerBody, local.speed.fast, { display: 'flex', opacity: 1 }, 'afterOpen')
          .to(local.el.drawerToggle, local.speed.fast, { rotation: '180deg' });

        return timeline;
      },

      initDrawerClose () {
        if (this.$mq('md-min AND md-max')) {
          local.activityIconMarginClosed = 70;
        }

        if (this.$mq('lg-min')) {
          local.activityIconMarginClosed = 120;
        }

        let timeline = new TimelineLite({ paused: true });

        timeline
          // While drawer closes
          // Step 1
          .to([local.el.drawerBody, local.el.drawerFilters], local.speed.fast, { display: 'none', opacity: 0 })
          .to(local.el.drawer, local.speed.fast, { width: local.collapsedWidth }, 'beforeClose')
          .to(local.el.drawerActivity, local.speed.fast, { marginLeft: local.activityIconMarginClosed }, 'beforeClose')
          // After drawer closes
          // Step 2
          .to(local.el.drawerHeader, local.speed.fast, { borderBottomWidth: '1px' }, 'afterClose')
          .to([local.el.drawerHeaderText, local.el.drawerList], local.speed.fast, { display: 'block', opacity: 1 }, 'afterClose')
          .to(local.el.drawerToggle, local.speed.fast, { rotation: '0deg' });

        return timeline;
      },

      animateDrawerOpen () {
        local.openDrawer.eventCallback('onStart', () => {
          this.isAnimating = true;
        });

        local.openDrawer.eventCallback('onComplete', () => {
          this.isAnimating = false;
        });

        this.$overlay.add(this.closeDrawer);

        local.openDrawer.restart();
      },

      animateDrawerClose () {
        local.closeDrawer.eventCallback('onStart', () => {
          this.isAnimating = true;
        });

        local.closeDrawer.eventCallback('onComplete', () => {
          this.isAnimating = false;
        });

        this.$overlay.remove();
        
        local.closeDrawer.restart();
      },

      animateActivity (cb) {
        const colors = [
          '#4158a3', // Blackjack
          '#ed414f', // Slots
          '#fac357', // Poker
          '#15a7d7', // Bingo
          '#18afa1', // Roulette
          '#9994C6', // Games
          '#D6589f', // Sports
          '#56c5d0', // Videoslots
        ]

        const randomColor = () => {
          let i = randomInt(0, colors.length);
          return colors[i];
        }

        const randomBlobs = (numBlobs) => {
          let blobTimeline = new TimelineLite();
          
          const r = local.el.drawerActivityCircle.offsetWidth / 2; // activity circle radius
          const xC = r; // activity circle x center
          const yC = r; // activity circle y center

          for (let i = numBlobs - 1; i >= 0; i--) {
            // we'll use the follow to place the blob on the edge of the activity circle
            const randomAngle = r + Math.random() * (0 - r);

            let x = xC + r * Math.cos(randomAngle);
            let y = yC + r * Math.sin(randomAngle);

            let xTranslateMax = 150;
            let yTranslateMax = 150;

            // Adjust placement and translation direction of blob if it falls in the lower quadrant of activity circle
            if (x < xC) {
              xTranslateMax = -150;
              x--;
            }

            if (y < yC) {
              yTranslateMax = -150;
              y--;
            }

            // create a blob div
            const div = document.createElement('div');

            div.style.left = x + 'px';
            div.style.top = y + 'px';
            div.classList.add('c-drawer__activity-blob');
            
            // add blob to the DOM
            local.el.drawerActivityCircle.appendChild(div);

            // add blob on timeline with random width, height, translateX & Y properties
            blobTimeline
              .to(div, 0.5, { scale: randomInt(1, 20), x:2, y:2, ease: RoughEase.ease.config({ strength: 1, points: 10, taper: "none", randomize: true, clamp: false}), force3D: false }, 'blobs')
              .to(div, 0.75, { x: randomInt(x, xTranslateMax), y: randomInt(y, yTranslateMax), backgroundColor: randomColor }, 'blobs+=0.5')
              .to(div, 0.25, { opacity: 0 }, '-=0.25')
              .call(function() {
                div.parentNode.removeChild(div);
              });
          }

          return blobTimeline;
        }

        let timeline = new TimelineLite();

        timeline
          // Setting force3D to false removes SVG bluring with scale
          .to(local.el.drawerActivityBolt, 0.25, { scale: 1.5, ease: Back.easeIn.config(3), force3D: false }) 
          .to(local.el.drawerActivityBolt, 0.25, { scale: 1 })
          .to(local.el.drawerActivityCircle, 0.25, { scale: 1.2, ease: Back.easeOut.config(3) })
          .call(randomBlobs, [20])
          .to(local.el.drawerActivityCircle, 0.5, { scale: 1 }, '+=0.75')
          .call(function() {
            if (typeof cb === 'function') {
              cb();
            }
          })
      },

      resetDrawer () {
        let newWindowWidth = document.documentElement.clientWidth;

        // Ensure the resize event is due to the window size changing
        // (iOS Safari has a tendancy to fire resize events when scrolling)
        if (this.windowWidth !== newWindowWidth) {
          // Update windowWidth
          this.windowWidth = newWindowWidth;

          // Setting reset to true will circumvent the watcher action on drawer.isDrawerActive
          this.reset = true;

          if (this.drawer.isDrawerActive) {
            this.$overlay.remove();
          }

          // Because reset is set to true, this will simply set the Vuex state to false
          this.closeDrawer()
              .then(() => {
                this.reset = false;
              });

          let targets = local.openDrawer.getChildren();

          // Destroy timelines
          local.openDrawer.kill();
          local.closeDrawer.kill();

          // Clear all styles on animated elements 
          targets.forEach((el) => {
            TweenLite.set(el.target, { clearProps: 'all' });
          });

          // Ensure all values are reset
          local.collapsedWidth = 0;
          local.extendedWidth = 0;
          local.activityIconMarginOpen = 0;
          local.activityIconMarginClosed = 0;

          // Re-initialize timelines
          local.openDrawer = this.initDrawerOpen();
          local.closeDrawer = this.initDrawerClose();
        }
      }
    },

    created () {     
      getGameTypesAsList().then(gameTypes => this.$store.commit('feed/RECEIVED_FEED_GAMETYPES', gameTypes));

      this.getFeed();

      let vm = this;
      // Start listening to Pusher notifications for new stories added to the feed
      Echo.channel('Feed').listen('StoryAddedToFeed', function (event) { 
        // Prepend new event objects to the feed
        // Here we will animate the activity feed icon before actually prepending the feed item
        vm.animateActivity(() => {
          vm.prependFeedItem(event);
        })
      });
    },

    mounted () {
      let vm = this;

      // Register DOM elements for drawer transitions
      local = { 
        el: {
          drawer: document.getElementById('drawer'),
          drawerToggle: document.getElementById('drawerToggle'),
          drawerToggleIcon: document.getElementById('drawerToggleIcon'),
          drawerHeader: document.getElementById('drawerHeader'),
          drawerActivity: document.getElementById('drawerActivity'),
          drawerActivityBolt: document.getElementById('drawerActivityBolt'),
          drawerActivityCircle: document.getElementById('drawerActivityCircle'),
          drawerActivityBlob: document.getElementById('drawerActivityBlob'),
          drawerHeaderText: document.getElementById('drawerHeaderText'),
          drawerList: document.getElementById('drawerList'),
          drawerFilters: document.getElementById('drawerFilters'),
          drawerBody: document.getElementById('drawerBody')
        },

        speed: {
          fast: 0.25,
          medium: 0.50,
          slow: 0.75
        },

        collapsedWidth: 0,
        extendedWidth: 0,
        activityIconMarginOpen: 0,
        activityIconMarginClosed: 0
      }

      local.openDrawer = this.initDrawerOpen();
      local.closeDrawer = this.initDrawerClose();

      // Used within resetDrawer function to determine if resize is intentional
      this.windowWidth = document.documentElement.clientWidth;

      let unwatch = this.$watch('allFeedData', () => {
        setTimeout(() => {
          vm.animateActivity();
        }, 2000);

        setTimeout(() => {
          vm.animateActivity();
        }, 2200);

        unwatch();
      })

      // Reset drawer when window is resized
      // Use debounce to ensure we only trigger the function once (within the 250ms threshold)
      window.addEventListener('resize', debounce(vm.resetDrawer, 250, false));
    },
  };
</script>



// WEBPACK FOOTER //
// ../../../../resources/assets/js/app/components/Drawer.vue